import { useCallback, useEffect, useRef, useState } from 'react';
import {
  forgotPassword,
  googleLoginUrl,
  login,
  register,
  resendCode,
  resetPassword,
  verifyCode,
} from '../lib/auth.js';
import { useModalBehaviour } from '../hooks/useModalBehaviour.js';
import { CodeInput } from './CodeInput.jsx';
import { GoogleIcon } from './GoogleIcon.jsx';

// Sign-in modal: Google OAuth plus a five-step email flow.
//
// The views are one modal rather than five because they hand state to each other
// — a failed sign-in can land on verification, verification can land on the
// password reset. `pendingEmail` is the address every later step operates on.
//
// This modal is deliberately not translated: none of its strings carried a
// data-i18n key in the original markup.

const VIEWS = { LOGIN: 'login', REGISTER: 'register', VERIFY: 'verify', FORGOT: 'forgot', RESET: 'reset' };

function Message({ msg }) {
  if (!msg) return <div className="auth-msg" />;
  return <div className={`auth-msg ${msg.type}`}>{msg.text}</div>;
}

export function AuthModal({ open, onClose, onAuthenticated }) {
  const [view, setView] = useState(VIEWS.LOGIN);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const [pendingEmail, setPendingEmail] = useState('');
  // Captured when a verification returns a resetToken instead of a session
  // token; the reset step re-verifies and prefers the fresh one.
  const pendingResetToken = useRef('');

  useModalBehaviour(open, onClose);

  // Closing resets to the sign-in view, so reopening never resumes half a flow.
  useEffect(() => {
    if (open) return;
    setView(VIEWS.LOGIN);
    setMsg(null);
    setBusy('');
    setCode('');
    setNewPassword('');
  }, [open]);

  const goto = useCallback((next, message = null) => {
    setView(next);
    setMsg(message);
    setCode('');
  }, []);

  const succeed = useCallback(
    (token) => {
      onClose();
      onAuthenticated(token);
    },
    [onClose, onAuthenticated]
  );

  // ── Sign in ──────────────────────────────────────────────────────────────
  // A failed sign-in is not necessarily a wrong password: the account may not
  // exist yet, or may exist but never have been verified. Registering and
  // resending are how those two are told apart, and both land on the code entry.
  const handleLogin = async () => {
    const addr = email.trim();
    if (!addr || !password) {
      setMsg({ type: 'error', text: 'Please enter email and password' });
      return;
    }
    setBusy('Signing in...');
    setMsg(null);
    try {
      const { data } = await login(addr, password);
      if (data.token) {
        succeed(data.token);
        return;
      }

      setBusy('Setting up account...');
      const { ok: regOk, data: regData } = await register({ email: addr, password });
      if (regData.success || regOk) {
        setPendingEmail(addr);
        goto(VIEWS.VERIFY, { type: 'success', text: 'Check your inbox for the verification code' });
      } else if (regData.code === 'VALIDATION_ERROR' && /already/i.test(regData.error || '')) {
        const { ok: resendOk, data: resendData } = await resendCode(addr);
        if (resendData.success || resendOk) {
          setPendingEmail(addr);
          goto(VIEWS.VERIFY, {
            type: 'success',
            text: 'A verification code has been sent to your email',
          });
        } else {
          // The account exists and is verified — the password really is wrong.
          setMsg({ type: 'error', text: data.error || 'Invalid email or password' });
        }
      } else {
        setMsg({ type: 'error', text: data.error || data.message || 'Login failed' });
      }
    } catch {
      setMsg({ type: 'error', text: 'Service unavailable' });
    }
    setBusy('');
  };

  // ── Register ─────────────────────────────────────────────────────────────
  const handleRegister = async () => {
    const addr = regEmail.trim();
    if (!addr || !regPassword) {
      setMsg({ type: 'error', text: 'Email and password are required' });
      return;
    }
    if (regPassword.length < 6) {
      setMsg({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }
    setBusy('Sending code...');
    setMsg(null);
    try {
      const body = { email: addr, password: regPassword };
      const name = regName.trim();
      if (name) body.name = name;
      const { ok, data } = await register(body);
      if (data.success || ok) {
        setPendingEmail(addr);
        goto(VIEWS.VERIFY, { type: 'success', text: 'Check your inbox for the verification code' });
      } else {
        setMsg({ type: 'error', text: data.error || data.message || 'Registration failed' });
      }
    } catch {
      setMsg({ type: 'error', text: 'Service unavailable' });
    }
    setBusy('');
  };

  // ── Verify ───────────────────────────────────────────────────────────────
  const handleVerify = async () => {
    if (code.length < 6) {
      setMsg({ type: 'error', text: 'Please enter the full 6-digit code' });
      return;
    }
    setBusy('Verifying...');
    setMsg(null);
    try {
      const { data } = await verifyCode(pendingEmail, code);
      if (data.token) {
        succeed(data.token);
        return;
      }
      if (data.resetToken) {
        // Verifying a password-reset code, not a sign-up code.
        pendingResetToken.current = data.resetToken;
        goto(VIEWS.RESET);
      } else {
        setMsg({ type: 'error', text: data.error || data.message || 'Verification failed' });
      }
    } catch {
      setMsg({ type: 'error', text: 'Service unavailable' });
    }
    setBusy('');
  };

  const handleResend = async () => {
    if (!pendingEmail) return;
    try {
      await resendCode(pendingEmail);
      setMsg({ type: 'success', text: 'Code resent' });
    } catch {
      setMsg({ type: 'error', text: 'Failed to resend' });
    }
  };

  // ── Forgot password ──────────────────────────────────────────────────────
  const handleForgot = async () => {
    const addr = forgotEmail.trim();
    if (!addr) {
      setMsg({ type: 'error', text: 'Please enter your email' });
      return;
    }
    setBusy('Sending...');
    setMsg(null);
    try {
      const { ok, data } = await forgotPassword(addr);
      if (data.success || ok) {
        setPendingEmail(addr);
        goto(VIEWS.RESET, { type: 'success', text: 'Check your inbox for the reset code' });
      } else {
        setMsg({ type: 'error', text: data.error || data.message || 'Failed' });
      }
    } catch {
      setMsg({ type: 'error', text: 'Service unavailable' });
    }
    setBusy('');
  };

  // ── Reset password ───────────────────────────────────────────────────────
  const handleReset = async () => {
    if (code.length < 6) {
      setMsg({ type: 'error', text: 'Enter the full 6-digit code' });
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      setMsg({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }
    setBusy('Resetting...');
    setMsg(null);
    try {
      // The code has to be exchanged for a resetToken before it can be spent.
      const { data: verifyData } = await verifyCode(pendingEmail, code);
      const resetToken = verifyData.resetToken || pendingResetToken.current;
      if (!resetToken) {
        setMsg({ type: 'error', text: verifyData.error || 'Invalid code' });
        setBusy('');
        return;
      }

      const { ok, data } = await resetPassword(resetToken, newPassword);
      if (data.success || ok) {
        setMsg({ type: 'success', text: 'Password reset! Signing in...' });
        const { data: loginData } = await login(pendingEmail, newPassword);
        if (loginData.token) {
          succeed(loginData.token);
          return;
        }
        goto(VIEWS.LOGIN, { type: 'success', text: 'Password reset. Please sign in.' });
      } else {
        setMsg({ type: 'error', text: data.error || data.message || 'Reset failed' });
      }
    } catch {
      setMsg({ type: 'error', text: 'Service unavailable' });
    }
    setBusy('');
  };

  const submitOnEnter = (handler) => (e) => {
    if (e.key === 'Enter' && !busy) handler();
  };

  return (
    <div
      className={`login-modal-overlay${open ? ' active' : ''}`}
      id="loginModal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="login-modal">
        <button className="close-modal" onClick={onClose}>
          &times;
        </button>
        <h2>Sign In</h2>

        <button className="google-btn" onClick={() => { window.location.href = googleLoginUrl(); }}>
          <GoogleIcon />
          Sign in with Google
        </button>

        <div className="divider">or sign in with email</div>

        {view === VIEWS.LOGIN && (
          <div>
            <input
              type="email"
              placeholder="Email address"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <input
              type="password"
              placeholder="Password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={submitOnEnter(handleLogin)}
            />
            <button className="submit-btn" disabled={!!busy} onClick={handleLogin}>
              {busy || 'Sign In'}
            </button>
            <Message msg={msg} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
              <button className="link-btn" onClick={() => goto(VIEWS.REGISTER)}>
                Create account
              </button>
              <button className="link-btn" onClick={() => goto(VIEWS.FORGOT)}>
                Forgot password?
              </button>
            </div>
          </div>
        )}

        {view === VIEWS.REGISTER && (
          <div>
            <input
              type="text"
              placeholder="Name (optional)"
              autoComplete="name"
              value={regName}
              onChange={(e) => setRegName(e.target.value)}
            />
            <input
              type="email"
              placeholder="Email address"
              autoComplete="email"
              value={regEmail}
              onChange={(e) => setRegEmail(e.target.value)}
            />
            <input
              type="password"
              placeholder="Password (min 6 characters)"
              autoComplete="new-password"
              value={regPassword}
              onChange={(e) => setRegPassword(e.target.value)}
              onKeyDown={submitOnEnter(handleRegister)}
            />
            <button className="submit-btn" disabled={!!busy} onClick={handleRegister}>
              {busy || 'Create Account'}
            </button>
            <Message msg={msg} />
            <div style={{ marginTop: 12, textAlign: 'center' }}>
              <button className="link-btn" onClick={() => goto(VIEWS.LOGIN)}>
                Back to sign in
              </button>
            </div>
          </div>
        )}

        {view === VIEWS.VERIFY && (
          <div>
            <p style={{ textAlign: 'center', fontSize: '0.9rem', color: '#666' }}>
              Enter the 6-digit code sent to
              <br />
              <strong>{pendingEmail}</strong>
            </p>
            <CodeInput value={code} onChange={setCode} />
            <button className="submit-btn" disabled={!!busy} onClick={handleVerify}>
              {busy || 'Verify'}
            </button>
            <Message msg={msg} />
            <div style={{ marginTop: 12, textAlign: 'center' }}>
              <button className="link-btn" onClick={handleResend}>
                Resend code
              </button>
            </div>
          </div>
        )}

        {view === VIEWS.FORGOT && (
          <div>
            <input
              type="email"
              placeholder="Email address"
              autoComplete="email"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              onKeyDown={submitOnEnter(handleForgot)}
            />
            <button className="submit-btn" disabled={!!busy} onClick={handleForgot}>
              {busy || 'Send Reset Code'}
            </button>
            <Message msg={msg} />
            <div style={{ marginTop: 12, textAlign: 'center' }}>
              <button className="link-btn" onClick={() => goto(VIEWS.LOGIN)}>
                Back to sign in
              </button>
            </div>
          </div>
        )}

        {view === VIEWS.RESET && (
          <div>
            <p style={{ textAlign: 'center', fontSize: '0.9rem', color: '#666' }}>
              Enter the code sent to <strong>{pendingEmail}</strong>
            </p>
            <CodeInput value={code} onChange={setCode} />
            <input
              type="password"
              placeholder="New password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              onKeyDown={submitOnEnter(handleReset)}
            />
            <button className="submit-btn" disabled={!!busy} onClick={handleReset}>
              {busy || 'Reset Password'}
            </button>
            <Message msg={msg} />
          </div>
        )}
      </div>
    </div>
  );
}
